import { $typst } from "@myriaddreamin/typst.ts/contrib/snippet";
import { preloadFontAssets } from "@myriaddreamin/typst.ts/options.init";
// The renderer wasm (~1 MiB) ships as a normal hashed asset. The compiler
// wasm is ~27 MiB raw — over the 25 MiB Pages per-file limit — so
// scripts/prepare-wasm.mjs gzips it into public/wasm/ (~9 MiB) and
// fetchCompilerModule() inflates it client-side. Everything stays
// same-origin (no CDN, CSP 'self'), same as the fonts in public/fonts/.
import rendererWasm from "@myriaddreamin/typst-ts-renderer/pkg/typst_ts_renderer_bg.wasm?url";

const COMPILER_WASM_GZ = "/wasm/typst_ts_web_compiler_bg.wasm.gz";

async function fetchCompilerModule(): Promise<ArrayBuffer> {
  const res = await fetch(COMPILER_WASM_GZ);
  if (!res.ok || !res.body) throw new Error(`wasm fetch failed: ${res.status}`);
  const buf = await res.arrayBuffer();
  const bytes = new Uint8Array(buf);
  // Gzip magic 1f 8b. If some proxy already transparently decoded the
  // body (or a future build ships it raw), pass the bytes through as-is.
  const isGzip = bytes.length > 2 && bytes[0] === 0x1f && bytes[1] === 0x8b;
  if (!isGzip) return buf;
  const ds = new DecompressionStream("gzip");
  const inflated = new Response(new Blob([buf]).stream().pipeThrough(ds));
  return inflated.arrayBuffer();
}

let configured = false;

function configure(): void {
  if (configured) return;
  configured = true;
  $typst.setCompilerInitOptions({
    beforeBuild: [preloadFontAssets({ assets: ["text"], assetUrlPrefix: "/fonts/" })],
    getModule: () => fetchCompilerModule(),
  });
  $typst.setRendererInitOptions({ getModule: () => rendererWasm });
}

export interface CompileResult {
  svg: string | null;
  error: string | null;
}

// Virtual path where the uploaded profile photo is mounted in the compiler
// FS. Templates reference it via `image("/profile.png")`. The UI normalises
// every upload to PNG before mapping so the format is always consistent.
export const PROFILE_IMAGE_PATH = "/profile.png";

// Keep the compiler's shadow FS in sync with the current upload. Passing
// null unmaps the file so `image("/profile.img")` errors cleanly (which is
// what we want if a template references a photo the user hasn't added).
let mappedImage = false;
async function syncProfileImage(bytes: Uint8Array | null): Promise<void> {
  if (bytes) {
    await $typst.mapShadow(PROFILE_IMAGE_PATH, bytes);
    mappedImage = true;
  } else if (mappedImage) {
    await $typst.unmapShadow(PROFILE_IMAGE_PATH).catch(() => {});
    mappedImage = false;
  }
}

/**
 * Compile Typst source to SVG for the live preview. Errors come back as a
 * message instead of throwing so the UI can keep the last good render on
 * screen while the user is mid-edit. `image` (raw bytes) is mounted at
 * PROFILE_IMAGE_PATH before compiling.
 */
export async function compileToSvg(
  source: string,
  image?: Uint8Array | null,
): Promise<CompileResult> {
  configure();
  try {
    await syncProfileImage(image ?? null);
    const svg = await $typst.svg({ mainContent: source });
    return { svg, error: null };
  } catch (e) {
    return { svg: null, error: formatTypstError(e) };
  }
}

/** Compile Typst source to PDF bytes for download. Throws on error. */
export async function compileToPdf(
  source: string,
  image?: Uint8Array | null,
): Promise<Uint8Array> {
  configure();
  await syncProfileImage(image ?? null);
  const pdf = await $typst.pdf({ mainContent: source });
  if (!pdf) throw new Error("pdf_failed");
  return pdf;
}

// The wasm compiler throws diagnostics in several shapes (string, Error,
// array of diagnostics). Flatten to something readable in the status bar.
function formatTypstError(e: unknown): string {
  if (typeof e === "string") return e;
  if (Array.isArray(e)) {
    return e
      .map((d) => (typeof d === "string" ? d : d?.message ?? JSON.stringify(d)))
      .join("\n");
  }
  if (e instanceof Error) return e.message;
  try {
    return JSON.stringify(e);
  } catch {
    return "error de compilación";
  }
}
